import React, { useState, useRef } from 'react';
import { Icons } from '../ui/Icons';
import { database } from '../../services/database';
import { ref, update, get } from 'firebase/database';
import { db } from '../../services/firebase';
import { toast } from 'react-hot-toast';
import { TaskCategory, TaskTemplate, TaskSubTaskTemplate } from '../../types';

interface ImportCategory {
    id?: string;
    name: string;
    color?: string;
    tasks?: ImportTask[];
}

interface ImportTask {
    id?: string;
    title?: string;
    name?: string;
    description?: string;
    steps?: ImportStep[];
}

interface ImportStep {
    id?: string;
    label?: string;
    name?: string;
    required?: boolean;
    subSteps?: (string | { text: string; label?: string })[];
}

interface ValidationError {
    type: 'error' | 'warning';
    message: string;
    item?: string;
}

const TaskImportView: React.FC = () => {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [importData, setImportData] = useState<ImportCategory[] | null>(null);
    const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);
    const [importMode, setImportMode] = useState<'file' | 'paste'>('file');
    const [pasteContent, setPasteContent] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isImporting, setIsImporting] = useState(false);

    const handleClear = () => {
        setImportData(null);
        setValidationErrors([]);
        setPasteContent('');
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const parseAndValidate = (jsonString: string) => {
        try {
            const json = JSON.parse(jsonString);
            setImportData(Array.isArray(json) ? json : null);
            if (!Array.isArray(json)) {
                toast.error('Invalid format: Root must be an array of categories');
                return;
            }
            const errors = validateData(json);
            setValidationErrors(errors);
        } catch (err) {
            toast.error('Invalid JSON structure');
            console.error(err);
        }
    };

    const validateData = (data: any[]): ValidationError[] => {
        const errors: ValidationError[] = [];
        if (!Array.isArray(data)) {
            errors.push({ type: 'error', message: 'Root must be an array of categories' });
            return errors;
        }

        data.forEach((cat, cIdx) => {
            const catName = cat.name || `Category ${cIdx + 1}`;
            if (!cat.name) errors.push({ type: 'error', message: 'Category missing name', item: catName });
            
            if (cat.tasks) {
                if (!Array.isArray(cat.tasks)) {
                    errors.push({ type: 'error', message: 'Tasks must be an array', item: catName });
                } else {
                    cat.tasks.forEach((task: any, tIdx: number) => {
                        const taskName = task.title || task.name || `Task ${tIdx + 1}`;
                        if (!task.title && !task.name) errors.push({ type: 'error', message: 'Task missing title', item: `${catName} > ${taskName}` });
                        
                        if (task.steps) {
                            if (!Array.isArray(task.steps)) {
                                errors.push({ type: 'error', message: 'Steps must be an array', item: taskName });
                            } else {
                                task.steps.forEach((step: any, sIdx: number) => {
                                    const stepName = step.label || step.name || `Step ${sIdx + 1}`;
                                    if (!step.label && !step.name) errors.push({ type: 'error', message: 'Step missing label', item: `${catName} > ${taskName} > ${stepName}` });
                                });
                            }
                        }
                    });
                }
            }
        });

        return errors;
    };

    const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (file.size > 2 * 1024 * 1024) {
            toast.error('File size exceeds 2MB limit');
            return;
        }

        const reader = new FileReader();
        reader.onload = (event) => {
            parseAndValidate(event.target?.result as string);
        };
        reader.readAsText(file);
    };

    const handlePasteImportAction = () => {
        if (!pasteContent.trim()) {
            toast.error('Please paste JSON content');
            return;
        }
        parseAndValidate(pasteContent);
    };

    const handleImport = async () => {
        if (!importData) return;
        if (validationErrors.some(e => e.type === 'error')) {
            toast.error('Please fix validation errors before importing');
            return;
        }

        setIsImporting(true);
        try {
            const timestamp = new Date().toISOString();
            const updates: any = {};
            let catCount = 0;
            let taskCount = 0;

            for (const cat of importData) {
                const catId = cat.id || `cat_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
                updates[`taskCategories/${catId}`] = {
                    id: catId,
                    name: cat.name,
                    color: cat.color || '#6366f1',
                    createdAt: timestamp,
                    updatedAt: timestamp
                };
                catCount++;

                if (cat.tasks && Array.isArray(cat.tasks)) {
                    for (const task of cat.tasks) {
                        const taskId = task.id || `tpl_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
                        const subTasks: TaskSubTaskTemplate[] = (task.steps || []).map((step, sIdx) => ({
                            label: step.label || step.name || 'Untitled Step',
                            order: sIdx + 1,
                            required: step.required ?? true,
                            subSteps: (step.subSteps || []).map((ss, ssIdx) => ({
                                name: typeof ss === 'string' ? ss : (ss.text || ss.label || 'Task'),
                                order: ssIdx + 1,
                                required: true // Default newly imported sub-steps to required for checklist enforcement
                            }))
                        }));

                        updates[`taskTemplates/${taskId}`] = {
                            id: taskId,
                            categoryId: catId,
                            title: task.title || task.name || 'Untitled Task',
                            description: task.description || '',
                            active: true,
                            subTasks,
                            createdAt: timestamp,
                            updatedAt: timestamp
                        };
                        taskCount++;
                    }
                }
            }

            await update(ref(db), updates);
            toast.success(`Successfully imported ${catCount} categories and ${taskCount} tasks`);
            handleClear();
        } catch (error) {
            console.error('Import error:', error);
            toast.error('Import failed. See console for details.');
        } finally {
            setIsImporting(false);
        }
    };

    return (
        <div className="flex flex-col gap-6 p-6 h-full overflow-y-auto custom-scrollbar">
            <div className="bg-white dark:bg-slate-800 rounded-3xl p-8 border border-gray-100 dark:border-slate-700 shadow-sm">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 mb-8">
                    <div>
                        <h2 className="text-2xl font-normal text-gray-900 dark:text-white uppercase tracking-tight flex items-center gap-3">
                            <Icons.Upload className="w-6 h-6 text-indigo-600" />
                            Import Tasks
                        </h2>
                        <p className="text-sm font-normal text-gray-400 uppercase tracking-widest mt-1">Bulk create categories and templates via JSON</p>
                    </div>
                </div>

                {!importData ? (
                    <div className="space-y-6">
                        {/* Tabs */}
                        <div className="flex bg-gray-100 dark:bg-slate-900/50 p-1 rounded-2xl w-fit">
                            <button
                                onClick={() => setImportMode('file')}
                                className={`px-6 py-2.5 rounded-xl text-xs font-normal uppercase tracking-widest transition-all ${
                                    importMode === 'file' 
                                    ? 'bg-white dark:bg-slate-800 text-indigo-600 shadow-sm' 
                                    : 'text-gray-400 hover:text-gray-600'
                                }`}
                            >
                                <div className="flex items-center gap-2">
                                    <Icons.Upload className="w-3.5 h-3.5" />
                                    Upload File
                                </div>
                            </button>
                            <button
                                onClick={() => setImportMode('paste')}
                                className={`px-6 py-2.5 rounded-xl text-xs font-normal uppercase tracking-widest transition-all ${
                                    importMode === 'paste' 
                                    ? 'bg-white dark:bg-slate-800 text-indigo-600 shadow-sm' 
                                    : 'text-gray-400 hover:text-gray-600'
                                }`}
                            >
                                <div className="flex items-center gap-2">
                                    <Icons.Copy className="w-3.5 h-3.5" />
                                    Paste JSON
                                </div>
                            </button>
                        </div>

                        {importMode === 'file' ? (
                            <div className="flex flex-col items-center justify-center p-16 border-4 border-dashed border-gray-50 dark:border-slate-700/50 rounded-[40px] text-gray-300">
                                <Icons.FileText className="w-20 h-20 mb-6 opacity-20" />
                                <p className="text-sm font-normal uppercase tracking-widest text-gray-400 mb-8">Drag & Drop or Select JSON File</p>
                                <input 
                                    type="file" 
                                    ref={fileInputRef}
                                    onChange={handleFileUpload}
                                    accept=".json"
                                    className="hidden"
                                />
                                <button 
                                    onClick={() => fileInputRef.current?.click()}
                                    className="px-8 py-4 bg-indigo-600 text-white text-[11px] font-normal rounded-2xl hover:bg-indigo-700 transition-all shadow-xl shadow-indigo-100 dark:shadow-none uppercase tracking-widest flex items-center gap-2"
                                >
                                    <Icons.FileText className="w-4 h-4" />
                                    Select JSON File
                                </button>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                <textarea
                                    value={pasteContent}
                                    onChange={(e) => setPasteContent(e.target.value)}
                                    placeholder="Paste your JSON here..."
                                    className="w-full h-80 bg-gray-50 dark:bg-slate-900 rounded-[32px] p-8 font-mono text-xs border-2 border-gray-100 dark:border-slate-700 focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all custom-scrollbar outline-none"
                                />
                                <div className="flex justify-center">
                                    <button 
                                        onClick={handlePasteImportAction}
                                        className="px-12 py-4 bg-indigo-600 text-white text-[11px] font-normal rounded-2xl hover:bg-indigo-700 transition-all shadow-xl shadow-indigo-100 dark:shadow-none uppercase tracking-widest flex items-center gap-2"
                                    >
                                        <Icons.Play className="w-4 h-4" />
                                        Preview Pasted Data
                                    </button>
                                </div>
                            </div>
                        )}

                        <div className="mt-8 text-left max-w-md mx-auto w-full bg-gray-50 dark:bg-slate-900/50 p-6 rounded-2xl border border-gray-100 dark:border-slate-700">
                            <p className="text-[10px] font-normal text-indigo-600 uppercase tracking-widest mb-4">Expected Structure Example:</p>
                            <pre className="text-[9px] font-mono text-gray-500 overflow-x-auto">
{`[
  {
    "name": "Front Desk",
    "tasks": [
      {
        "title": "Night Audit",
        "description": "Daily audit procedures",
        "steps": [
          {
            "label": "Preparation",
            "required": true,
            "subSteps": ["Check keys", "Verify arrivals"]
          }
        ]
      }
    ]
  }
]`}
                            </pre>
                        </div>
                    </div>
                ) : (
                    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-2 duration-300">
                        {/* Validation Panel */}
                        {validationErrors.length > 0 && (
                            <div className="bg-amber-50 dark:bg-amber-900/10 p-6 rounded-3xl border border-amber-100 dark:border-amber-900/30">
                                <h3 className="text-xs font-normal text-amber-600 dark:text-amber-500 uppercase tracking-widest mb-4 flex items-center gap-2">
                                    <Icons.AlertTriangle className="w-4 h-4" />
                                    Validation Issues ({validationErrors.length})
                                </h3>
                                <div className="space-y-2 max-h-40 overflow-y-auto pr-2 custom-scrollbar">
                                    {validationErrors.map((err, idx) => (
                                        <div key={idx} className="flex items-start gap-3 bg-white dark:bg-slate-800 p-3 rounded-xl border border-amber-100 dark:border-amber-900/20 shadow-sm">
                                            <div className={`w-1.5 h-1.5 rounded-full mt-1.5 ${err.type === 'error' ? 'bg-red-500' : 'bg-amber-500'}`} />
                                            <div>
                                                <p className="text-[11px] font-normal text-gray-700 dark:text-gray-300">{err.message}</p>
                                                {err.item && <p className="text-[9px] text-gray-400 font-normal uppercase tracking-widest mt-0.5">{err.item}</p>}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Preview Section */}
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                            <div className="space-y-4">
                                <h3 className="text-xs font-normal text-gray-400 uppercase tracking-widest ml-4">Import Preview</h3>
                                <div className="space-y-6 max-h-[500px] overflow-y-auto pr-2 custom-scrollbar p-2">
                                    {importData.map((cat, cIdx) => (
                                        <div key={cIdx} className="bg-gray-50 dark:bg-slate-900/30 rounded-3xl p-6 border border-gray-100 dark:border-slate-700 shadow-sm">
                                            <div className="flex items-center gap-3 mb-6">
                                                <div className="w-4 h-4 rounded-full shadow-sm" style={{ backgroundColor: cat.color || '#6366f1' }} />
                                                <h4 className="text-base font-normal text-gray-900 dark:text-white uppercase tracking-tight">Category: {cat.name}</h4>
                                            </div>
                                            <div className="space-y-4 pl-8 border-l-2 border-indigo-100 dark:border-indigo-900/30">
                                                {(cat.tasks || []).map((task, tIdx) => (
                                                    <div key={tIdx} className="bg-white dark:bg-slate-800 p-5 rounded-2xl border border-gray-100 dark:border-slate-700 shadow-sm overflow-hidden relative">
                                                        <div className="absolute top-0 right-0 w-16 h-16 bg-gray-50 dark:bg-slate-900/50 -mr-8 -mt-8 rotate-45" />
                                                        <div className="relative z-10">
                                                            <p className="text-xs font-normal text-indigo-600 uppercase tracking-widest mb-3">Task: {task.title || task.name}</p>
                                                            <div className="space-y-4">
                                                                {(task.steps || []).map((step, sIdx) => (
                                                                    <div key={sIdx} className="space-y-2">
                                                                        <div className="flex items-center gap-2 text-[10px] font-normal text-gray-700 dark:text-gray-300 uppercase tracking-tight">
                                                                            <div className="w-1.5 h-1.5 bg-indigo-500 rounded-full" />
                                                                            Step: {step.label || step.name}
                                                                            {step.required && <span className="text-indigo-400 font-normal ml-1 text-[8px] border border-indigo-100 px-1 rounded">REQ</span>}
                                                                        </div>
                                                                         <div className="ml-4 space-y-1">
                                                                            {(step.subSteps || []).map((ss, idx) => (
                                                                                <div key={idx} className="flex items-center gap-2 text-[9px] text-gray-400 font-normal uppercase tracking-widest italic ml-2">
                                                                                    <span className="text-indigo-300">•</span>
                                                                                    {typeof ss === 'string' ? ss : (ss.text || ss.label)}
                                                                                </div>
                                                                            ))}
                                                                        </div>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Action Summary */}
                            <div className="bg-indigo-600 rounded-[40px] p-10 text-white shadow-2xl flex flex-col h-fit sticky top-6">
                                <div className="mb-10 text-center">
                                    <div className="w-20 h-20 bg-white/10 rounded-3xl flex items-center justify-center mx-auto mb-6 backdrop-blur-sm border border-white/20">
                                        <Icons.Rocket className="w-10 h-10" />
                                    </div>
                                    <h3 className="text-3xl font-normal uppercase tracking-tight">Import Ready</h3>
                                    <p className="text-white/60 text-xs font-normal uppercase tracking-widest mt-2">Check stats before finalizing</p>
                                </div>

                                <div className="grid grid-cols-2 gap-4 mb-10">
                                    <div className="bg-white/10 backdrop-blur-md p-5 rounded-3xl border border-white/20">
                                        <p className="text-[10px] font-normal uppercase tracking-widest opacity-60 mb-1">Categories</p>
                                        <p className="text-3xl font-normal">{importData.length}</p>
                                    </div>
                                    <div className="bg-white/10 backdrop-blur-md p-5 rounded-3xl border border-white/20">
                                        <p className="text-[10px] font-normal uppercase tracking-widest opacity-60 mb-1">Total Tasks</p>
                                        <p className="text-3xl font-normal">{importData.reduce((sum, cat) => sum + (cat.tasks?.length || 0), 0)}</p>
                                    </div>
                                </div>

                                <div className="flex gap-3">
                                    <button 
                                        onClick={handleClear}
                                        className="flex-1 py-4 bg-white/10 hover:bg-white/20 text-white text-[11px] font-normal rounded-2xl transition-all uppercase tracking-widest"
                                    >
                                        Discard
                                    </button>
                                    <button 
                                        onClick={handleImport}
                                        disabled={isImporting || validationErrors.some(e => e.type === 'error')}
                                        className="flex-[2] py-4 bg-white text-indigo-600 text-[11px] font-normal rounded-2xl hover:bg-indigo-50 transition-all uppercase tracking-widest flex items-center justify-center gap-2 shadow-xl shadow-indigo-950/20 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        {isImporting ? (
                                            <Icons.Loader2 className="w-5 h-5 animate-spin" />
                                        ) : (
                                            <>
                                                <Icons.Database className="w-5 h-5" />
                                                Confirm Import
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default TaskImportView;
